import { FC, ReactNode, useEffect, useRef } from 'react'
import cn from 'classnames'

import styles from './styles.module.scss'

interface Props {
  onCaptchaSuccess: (token: string) => void
  className?: string
  children: ReactNode
  isLoading?: boolean
  setIsLoading?: (val: boolean) => void
  autoExecute?: boolean
}

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        options: {
          sitekey: string
          callback: (token: string) => void
          'error-callback'?: () => void
          theme?: 'light' | 'dark'
          action?: string
          size?: string
          execution?: string
          appearance?: string
        },
      ) => string
      execute: (id: string) => void
      reset: (id: string) => void
    }
  }
}

export const Captcha: FC<Props> = ({ onCaptchaSuccess, className, children, setIsLoading, autoExecute = false }) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string>()
  const renderedRef = useRef(false)
  const busyRef = useRef(false)

  useEffect(() => {
    const render = () => {
      if (renderedRef.current || !wrapperRef.current || !window.turnstile) return

      widgetIdRef.current = window.turnstile.render(wrapperRef.current, {
        sitekey: '0x4AAAAAABhWI4E5xFC8bjd-',
        theme: 'light',
        action: 'submit',

        execution: 'execute',
        appearance: 'execute',

        callback: (token: string) => {
          busyRef.current = false
          window.turnstile!.reset(widgetIdRef.current!)
          onCaptchaSuccess(token)
        },

        'error-callback': () => {
          busyRef.current = false
          window.turnstile!.reset(widgetIdRef.current!)
        },
      })

      renderedRef.current = true

      if (autoExecute) {
        setTimeout(() => {
          if (widgetIdRef.current && window.turnstile) {
            busyRef.current = true
            window.turnstile.execute(widgetIdRef.current)
          }
        }, 100) // можно задержку оставить на всякий случай
      }
    }

    if (window.turnstile) render()
    else {
      const s = document.createElement('script')
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      s.async = true
      s.onload = render
      document.body.appendChild(s)
    }
  }, [onCaptchaSuccess])

  const handleClick = () => {
    if (setIsLoading) setIsLoading(true)
    if (!widgetIdRef.current || !window.turnstile) return
    if (busyRef.current) return

    busyRef.current = true
    window.turnstile.execute(widgetIdRef.current)
  }

  return (
    <div className={cn(styles.captchaWrapper, className)} onClick={handleClick}>
      <div
        ref={wrapperRef}
        style={{
          position: 'absolute',
          left: '-9999px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      />
      {children}
    </div>
  )
}
